import type { SyncCheckResponse, SyncIssue } from '@/services/fixedAssets'
import { assetClassFromQboAccountName } from './fixedAssetConstants'
import type { FixedAssetModalPrefill } from './FixedAssetModal'
import { formatFixedAssetMoney } from './fixedAssetsFormat'

/** Single-line copy for an unmapped-account sync issue, with $#,##0.00 balance. */
export function formatUnmappedAccountIssue(issue: SyncIssue): string {
  const balance = formatFixedAssetMoney(issue.delta)
  const withoutBalance = issue.message.replace(/\s*\(balance [^)]+\)\s*$/, '')
  if (withoutBalance !== issue.message) {
    return `${withoutBalance} (balance ${balance})`
  }
  return `Unmapped account '${issue.name}' (balance ${balance})`
}

export function isActionableSyncIssue(issue: SyncIssue): boolean {
  return issue.severity === 'error' || issue.severity === 'warning'
}

export function infoSyncIssues(issues: SyncIssue[]): SyncIssue[] {
  return issues.filter((issue) => issue.severity === 'info')
}

export function renameNoticeText(issues: SyncIssue[]): string | null {
  const count = infoSyncIssues(issues).length
  if (count === 0) return null
  if (count === 1) {
    return '1 linked account was renamed in QuickBooks. Names update automatically — no action needed.'
  }
  return `${count} linked accounts were renamed in QuickBooks. Names update automatically — no action needed.`
}

export function prefillFromUnmappedIssue(
  issue: SyncIssue,
  snapshot?: SyncCheckResponse['account_snapshot'] | null,
): FixedAssetModalPrefill {
  const today = new Date().toISOString().slice(0, 10)
  const snapshotBalance = snapshot?.[issue.qbo_account_id]?.balance
  const balance = snapshotBalance || issue.delta || '0'
  return {
    name: issue.name,
    asset_account_qbo_id: issue.qbo_account_id,
    purchase_price: balance,
    purchase_date: today,
    dep_start_date: today,
    asset_class: assetClassFromQboAccountName(issue.name),
  }
}
